/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Switch, TextField,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { meAPI } from '../../api/crm'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from './NotificationProvider'
import DeviceSettings from './DeviceSettings'
import { useProfile } from '../../hooks/useCrmQueries'

const MAX_NAME = 100

/** Set or change the signed-in person's display name (shown to everyone, in every workspace). */
const ProfileDialog = ({ open, onClose, initialName = '', prompt = false }) => {
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { data: profile } = useProfile()
  const setPhoto = useMutation({
    mutationFn: (hidden) => meAPI.setPhotoHidden(hidden),
    onSuccess: (p) => {
      queryClient.setQueryData(['me', 'profile'], p)
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey.includes('members') })
      notify(p.photoHidden ? 'Photo hidden' : 'Photo shown')
    },
  })
  const [name, setName] = useState(initialName)
  const trimmed = name.trim()
  const digest = useQuery({ queryKey: ['me', 'digest'], queryFn: meAPI.digest, enabled: open && !prompt })
  const setDigest = useMutation({
    mutationFn: (weekly) => meAPI.setDigest(weekly),
    onSuccess: (d) => { queryClient.setQueryData(['me', 'digest'], d); notify(d.weekly ? 'Weekly summary on' : 'Weekly summary off') },
  })

  const save = useMutation({
    mutationFn: () => meAPI.updateProfile(trimmed),
    onSuccess: (profile) => {
      queryClient.setQueryData(['me', 'profile'], profile)
      // names appear in member lists, people fields and timelines of every workspace
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey.includes('members') || q.queryKey.includes('records') })
      notify('Name saved')
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{prompt ? 'What should we call you?' : 'Your name'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Your teammates see this name on records, tasks and activity instead of your email.
        </DialogContentText>
        {save.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(save.error)}</Alert>}
        <TextField
          autoFocus fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && trimmed) save.mutate() }}
          error={name.length > MAX_NAME} helperText={name.length > MAX_NAME ? `At most ${MAX_NAME} characters` : ' '}
          inputProps={{ maxLength: MAX_NAME + 20 }}
        />
        {!prompt && (profile?.pictureUrl || profile?.photoHidden) && (
          <FormControlLabel sx={{ mt: 1, display: 'flex' }}
            control={<Switch checked={!profile.photoHidden} disabled={setPhoto.isPending} onChange={(e) => setPhoto.mutate(!e.target.checked)} />}
            label="Show the photo from my Google / Microsoft account to my teammates" />
        )}
        {!prompt && digest.data && (
          <FormControlLabel sx={{ mt: 1 }}
            control={<Switch checked={digest.data.weekly} disabled={setDigest.isPending} onChange={(e) => setDigest.mutate(e.target.checked)} />}
            label="Weekly summary email on Mondays (new records, your tasks)" />
        )}
        {!prompt && <DeviceSettings />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{prompt ? 'Later' : 'Cancel'}</Button>
        <Button variant="contained" disabled={!trimmed || name.length > MAX_NAME || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProfileDialog
